import { enums } from "@shared/constants";
import { ShippingMethodsTypeMapIdType } from "@shared/constants/enums/enums-types";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { VenueBasedProductSerializer } from "@app-contexts/dashboard/features/products/data/types";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import { VenueBasedTicketTypeInventorySerializer } from "../../data/types/inventory";

/** returns ticket type ids that have single item in shipping_type array, and it is equal shippingType */
export const getTicketTypeItemsIdsByShippingType = (
  items:
    | VenueBasedTicketTypeInventorySerializer[]
    | Exclude<PublicBaseEventSerializer["ticket_types"], undefined>,
  shippingType: ShippingMethodsTypeMapIdType
): number[] => {
  const ids: number[] = [];

  items.forEach((item) => {
    const ticketType = "ticket_type" in item ? item.ticket_type : item;

    if (
      ticketType?.shipping_type?.length &&
      ticketType?.shipping_type?.length === 1 &&
      ticketType?.shipping_type[0] === shippingType
    ) {
      ids.push(ticketType.id);
    }
  });

  return ids;
};

/** returns item groups with single shipping_type */
export const getItemsGroupsByShippingType = (
  ticketTypeItems:
    | VenueBasedTicketTypeInventorySerializer[]
    | Exclude<PublicBaseEventSerializer["ticket_types"], undefined>,
  itemGroups: TicketItemGroupType[],
  shippingType: ShippingMethodsTypeMapIdType
): TicketItemGroupType[] => {
  const ticketTypeIds = getTicketTypeItemsIdsByShippingType(
    ticketTypeItems,
    shippingType
  );

  return itemGroups.filter(
    (group) =>
      group?.type && ticketTypeIds.includes(group.type) && !group?.parent
  );
};

/** returns uniq shipping methods arrays if their number more then 1 */
export const getUniqShippingTypesGroups = (
  ticketTypeItems:
    | VenueBasedTicketTypeInventorySerializer[]
    | Exclude<PublicBaseEventSerializer["ticket_types"], undefined>,
  productItems: (VenueBasedProductSerializer | ProductTypeSerializer)[],
  membershipItems: (
    | VenueBasedMembershipGroupSerializer
    | PublicMembershipGroupSerializer
  )[] = []
): Array<ShippingMethodsTypeMapIdType[]> => {
  const shippingTypes: Array<ShippingMethodsTypeMapIdType[]> = [];
  const PRINT_ID = enums.SHIPPING_METHODS.PRINT.id;
  const DELIVERY_ID = enums.SHIPPING_METHODS.DELIVERY.id;
  const WILL_CALL_ID = enums.SHIPPING_METHODS.WILL_CALL.id;

  const SORT_ORDER = [PRINT_ID, DELIVERY_ID, WILL_CALL_ID];

  // Sort using predefined order: st_print, st_delivery, st_will_call.
  // `types` will never be a large array so the performance hit is negligible.
  const sortShippingTypes = (types: ShippingMethodsTypeMapIdType[]) => {
    return uniq(types).sort(
      (a, b) => SORT_ORDER.indexOf(a) - SORT_ORDER.indexOf(b)
    );
  };

  // Process ticket types
  ticketTypeItems.forEach((item) => {
    const ticketType = "ticket_type" in item ? item.ticket_type : item;
    if (
      ticketType?.shipping_type?.length &&
      ticketType.shipping_type.length > 1
    ) {
      shippingTypes.push(
        sortShippingTypes(
          ticketType.shipping_type as ShippingMethodsTypeMapIdType[]
        )
      );
    }
  });

  // Process product items
  productItems.forEach((item) => {
    if (item?.shipping_type?.length && item.shipping_type.length > 1) {
      shippingTypes.push(
        sortShippingTypes(item.shipping_type as ShippingMethodsTypeMapIdType[])
      );
    }
  });

  // Process membership items
  membershipItems.forEach((group) => {
    if (group.membership_levels && Array.isArray(group.membership_levels)) {
      group.membership_levels.forEach((level) => {
        if (
          level &&
          "shipping_type" in level &&
          Array.isArray(level.shipping_type) &&
          level.shipping_type.length > 1
        ) {
          shippingTypes.push(
            sortShippingTypes(
              level.shipping_type as ShippingMethodsTypeMapIdType[]
            )
          );
        }
      });
    }
  });

  // uniq shipping types arrays
  // for [["sp_print", "sp_will_call"], ["sp_delivery, "sp_will_call"], ["sp_print", "sp_will_call"]]
  // returns [["sp_print", "sp_will_call"], ["sp_delivery, "sp_will_call"]]
  return uniqBy(shippingTypes, (arr: ShippingMethodsTypeMapIdType[]) =>
    JSON.stringify(arr)
  );
};

/** returns array of object that includes array of shipping types and ticket type ids for witch they can be applied
 * Example:
 * [
 *   {
 *      ids: [260, 262],
 *      shippingTypesGroup: ["st_delivery", "st_print", "st_will_call"],
 *   },
 *   { ids: [261], shippingTypesGroup: ["st_delivery", "st_print"] },
 * ]
 */
export const getTicketTypesIdsByShippingTypeGroups = (
  ticketTypeItems:
    | VenueBasedTicketTypeInventorySerializer[]
    | Exclude<PublicBaseEventSerializer["ticket_types"], undefined>,
  productItems: (VenueBasedProductSerializer | ProductTypeSerializer)[],
  membershipItems: (
    | VenueBasedMembershipGroupSerializer
    | PublicMembershipGroupSerializer
  )[] = []
) => {
  const shippingTypesGroups = getUniqShippingTypesGroups(
    ticketTypeItems,
    productItems,
    membershipItems
  );

  const shippingTypes: Array<{
    shippingTypesGroup: ShippingMethodsTypeMapIdType[];
    ids: number[];
  }> = [];

  shippingTypesGroups.forEach((group, index) => {
    shippingTypes.push({
      shippingTypesGroup: group,
      ids: [],
    });

    ticketTypeItems.forEach((item) => {
      const ticketType = "ticket_type" in item ? item.ticket_type : item;
      if (
        ticketType?.shipping_type?.length === group.length &&
        ticketType.shipping_type.every((type: ShippingMethodsTypeMapIdType) =>
          group.includes(type)
        )
      ) {
        shippingTypes[index].ids.push(ticketType.id);
      }
    });

    productItems.forEach((item) => {
      if (
        item.shipping_type?.length === group.length &&
        item.shipping_type.every((type: ShippingMethodsTypeMapIdType) =>
          group.includes(type)
        )
      ) {
        shippingTypes[index].ids.push(item.id);
      }
    });

    // For membership items, we need to check membership_levels
    membershipItems.forEach((membershipGroup) => {
      if (
        membershipGroup.membership_levels &&
        Array.isArray(membershipGroup.membership_levels)
      ) {
        membershipGroup.membership_levels.forEach((level) => {
          if (
            level &&
            "shipping_type" in level &&
            Array.isArray(level.shipping_type) &&
            level.shipping_type.length === group.length &&
            level.shipping_type.every((type: ShippingMethodsTypeMapIdType) =>
              group.includes(type)
            )
          ) {
            shippingTypes[index].ids.push(level.id);
          }
        });
      }
    });
  });

  return shippingTypes;
};

/** return example:
 * [{
 *  itemGroups: [
 *    {
 *     id: 2636,
 *     type: 261,
 *     event: 177,
 *     item_type: "ticket",
 *     quantity: 1,
 *     description:
 *     "Fourth (electronic and standard) - Event for delivery/fulfillment options testing",
 *     shipping_type: "st_print",
 *    },
 * ],
 *  shippingTypes: ["st_delivery", "st_print"],
 * }]
 */
export const getItemsGroupsByMixedShippingTypes = (
  ticketTypeItems:
    | VenueBasedTicketTypeInventorySerializer[]
    | Exclude<PublicBaseEventSerializer["ticket_types"], undefined>,
  productsItems: (VenueBasedProductSerializer | ProductTypeSerializer)[],
  membershipItems: (
    | VenueBasedMembershipGroupSerializer
    | PublicMembershipGroupSerializer
  )[] = [],
  itemGroups: TicketItemGroupType[]
) => {
  const shippingTypesGroups = getTicketTypesIdsByShippingTypeGroups(
    ticketTypeItems,
    productsItems,
    membershipItems
  );

  const itemGroupsByShippingTypeGroups: Array<{
    shippingTypes: ShippingMethodsTypeMapIdType[];
    itemGroups: TicketItemGroupType[];
  }> = [];

  shippingTypesGroups.forEach((typesGroup, index) => {
    itemGroups.forEach((group) => {
      if (
        (group?.type &&
          typesGroup.ids.includes(group.type) &&
          !group?.parent) ||
        (group?.product && typesGroup.ids.includes(group.product)) ||
        (group?.membership_level &&
          typesGroup.ids.includes(group.membership_level))
      ) {
        // create array element if shipping method can be applied to itemGroup
        if (!itemGroupsByShippingTypeGroups[index]) {
          itemGroupsByShippingTypeGroups[index] = {
            shippingTypes: typesGroup.shippingTypesGroup,
            itemGroups: [],
          };
        }
        // write item group
        itemGroupsByShippingTypeGroups[index].itemGroups.push(group);
      }
    });
  });

  return itemGroupsByShippingTypeGroups;
};

export const getShippingTypeTranslation = (
  type: ShippingMethodsTypeMapIdType
) => {
  return enums.SHIPPING_METHODS_BY_ID[type].name;
};

export const getShippingTypeColorScheme = (
  type: ShippingMethodsTypeMapIdType
) => {
  switch (type) {
    case enums.SHIPPING_METHODS.WILL_CALL.id:
    case enums.SHIPPING_METHODS.DELIVERY.id:
      return "blue";
    case enums.SHIPPING_METHODS.PRINT.id:
    default:
      return "secondary";
  }
};

export const getProductsItemsIdsByShippingType = (
  productItems: (VenueBasedProductSerializer | ProductTypeSerializer)[],
  shippingType: ShippingMethodsTypeMapIdType
): number[] => {
  const ids: number[] = [];

  productItems.forEach((item) => {
    if (
      Array.isArray(item?.shipping_type) &&
      item.shipping_type?.length &&
      item.shipping_type.length === 1 &&
      item.shipping_type[0] === shippingType
    ) {
      ids.push(item.id);
    }
  });

  return ids;
};

export const getProductsItemsGroupsByShippingType = (
  productItems: (VenueBasedProductSerializer | ProductTypeSerializer)[],
  itemGroups: TicketItemGroupType[],
  shippingType: ShippingMethodsTypeMapIdType
): TicketItemGroupType[] => {
  const ticketTypeIds = getProductsItemsIdsByShippingType(
    productItems,
    shippingType
  );

  return itemGroups.filter(
    (group) => group?.product && ticketTypeIds.includes(group.product)
  );
};

/**
 * Returns membership level IDs that have a single shipping type matching the provided shipping type
 */
export const getMembershipItemsIdsByShippingType = (
  membershipItems: (
    | VenueBasedMembershipGroupSerializer
    | PublicMembershipGroupSerializer
  )[],
  shippingType: ShippingMethodsTypeMapIdType
): number[] => {
  const ids: number[] = [];

  membershipItems.forEach((item) => {
    // Each membership group has multiple membership_levels
    if (item.membership_levels && Array.isArray(item.membership_levels)) {
      // Check each membership level for matching shipping_type
      item.membership_levels.forEach((level) => {
        // Access the shipping_type property and ensure it exists
        if (
          level &&
          "shipping_type" in level &&
          Array.isArray(level.shipping_type) &&
          level.shipping_type.length === 1 &&
          level.shipping_type[0] === shippingType
        ) {
          // Add the membership level ID (not the group ID)
          ids.push(level.id);
        }
      });
    }
  });

  return ids;
};

/**
 * Returns membership item groups filtered by shipping type
 */
export const getMembershipItemsGroupsByShippingType = (
  membershipItems: (
    | VenueBasedMembershipGroupSerializer
    | PublicMembershipGroupSerializer
  )[],
  itemGroups: TicketItemGroupType[],
  shippingType: ShippingMethodsTypeMapIdType
): TicketItemGroupType[] => {
  const membershipItemIds = getMembershipItemsIdsByShippingType(
    membershipItems,
    shippingType
  );

  return itemGroups.filter(
    (group) =>
      group?.membership_level &&
      membershipItemIds.includes(group.membership_level)
  );
};
